import { Icon } from '@iconify/react';

import Button, { type ButtonProps } from '@mui/material/Button';

// ----------------------------------------------------------------------

type ActionButtonProps = {
  label: string;
  testId: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: Exclude<ButtonProps['variant'], undefined>;
  size?: 'small' | 'medium' | 'large';
  styleVariant?: 'dark-contained' | 'light-outlined';
  icon?: string;
  iconWidth?: number;
  iconPosition?: 'start' | 'end';
};

export function ActionButton({
  label,
  testId,
  onClick,
  disabled = false,
  variant,
  size = 'medium',
  styleVariant = 'dark-contained',
  icon = 'lucide:file-input',
  iconWidth,
  iconPosition,
}: Readonly<ActionButtonProps>) {
  const isLightOutlined = styleVariant === 'light-outlined';
  const resolvedVariant = variant ?? (isLightOutlined ? 'outlined' : 'contained');
  const isTextVariant = resolvedVariant === 'text';
  const resolvedIconWidth = iconWidth ?? (isLightOutlined ? 18 : 22);
  const resolvedIconPosition = iconPosition ?? (isLightOutlined ? 'start' : 'end');
  let buttonSx;

  if (isTextVariant) {
    buttonSx = {
      color: 'text.primary',
      px: 0,
      minWidth: 'auto',
      whiteSpace: 'nowrap',
      '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: 'text.primary' },
      '&:hover': {
        bgcolor: 'action.hover',
        textDecoration: 'none',
      },
    };
  } else if (isLightOutlined) {
    buttonSx = {
      borderColor: 'primary.main',
      color: 'text.primary',
      bgcolor: 'transparent',
      whiteSpace: 'nowrap',
      '& .MuiButton-startIcon': { color: 'primary.main' },
      '&:hover': {
        borderColor: 'primary.dark',
        bgcolor: 'action.hover',
      },
    };
  } else {
    buttonSx = {
      bgcolor: 'grey.800',
      color: 'common.white',
      whiteSpace: 'nowrap',
      '&:hover': { bgcolor: 'grey.700' },
    };
  }

  return (
    <Button
      variant={resolvedVariant}
      color={isLightOutlined || isTextVariant ? 'primary' : 'inherit'}
      size={size}
      {...(resolvedIconPosition === 'start'
        ? { startIcon: <Icon icon={icon} width={resolvedIconWidth} /> }
        : { endIcon: <Icon icon={icon} width={resolvedIconWidth} /> })}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      sx={buttonSx}
    >
      {label}
    </Button>
  );
}
